import React, { Fragment, useRef, useState } from 'react'
import Card from '../../../../../components/atoms/Card'
import Container from '../../../../../components/atoms/Container'
import Heading from '../../../../../components/atoms/Heading'
import Button from '../../../../../components/molecules/Button'
import { useTranslation } from '../../../../../providers/I18n'
import { BankCreditDetail } from '../../../../../types/credits'
import { StyledDescription } from '../../../../../components/styled/Description'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import {
  addCreditHistory,
  getCredit,
  updateCredit,
} from '../../../../../services/credits'
import { getCreditColor } from '../../../../../utils/credits'
import { useUser } from '../../../../../providers/User'
import CreditWaitAction from '../../../shared/CreditWaitAction'
import Textarea from '../../../../../components/atoms/Textarea'
import { CreditPhases, CreditSteps, Role } from '../../../../../types/enums'
import Spinner from '../../../../../components/atoms/Spinner'
import DateInput from '../../../../../components/atoms/DateInput'
import NumericInput from '../../../../../components/atoms/NumericInput'
import Input from '../../../../../components/atoms/Input'
import { formatCurrency } from '../../../../../utils/currency'
import { useAlert } from '../../../../../providers/Alert'

const Phase3Escriturado: React.FC = () => {
  const commentRef = useRef<HTMLTextAreaElement>(null)
  const finalSignatureDateRef = useRef<HTMLInputElement>(null)
  const finalCreditAmountRef = useRef<HTMLInputElement>(null)
  const escrituraNumberRef = useRef<HTMLInputElement>(null)
  const { code } = useParams()
  const { user } = useUser()
  const { setAlert } = useAlert()
  const [loading, setLoading] = useState<boolean>(false)
  const { getTranslation } = useTranslation()
  const {
    data: credit,
    isLoading,
    refetch,
  } = useQuery<BankCreditDetail>(['getCredit', code], () => getCredit(code!), {
    refetchOnWindowFocus: false,
  })

  let header
  let footer
  let content
  if (!credit && isLoading) {
    content = (
      <Container>
        <StyledDescription>Cargando credito...</StyledDescription>
      </Container>
    )
  } else if (credit) {
    const isActiveOwner = user!.code === credit.owners.phase3Operator?.code
    const color = getCreditColor(credit.phase)
    const creditPhaseStep = `${getTranslation!(
      `phases.${credit.phase}.title`
    )} - ${getTranslation!(`phases.${credit.phase}.steps.${credit.step}`)}`

    header = (
      <Container padding>
        <Heading type="3" color={color}>
          {creditPhaseStep}
        </Heading>
      </Container>
    )

    if (isActiveOwner || user?.role !== Role.Advisor) {
      const handleSendToFondeo = async () => {
        setLoading(true)

        const hasComment = !!commentRef.current?.value
        const hasEscrituraNumber = !!escrituraNumberRef.current?.value
        const hasCreditAmount = !!finalCreditAmountRef.current?.value
        const hasDate = !!finalSignatureDateRef.current?.value
        if (
          !hasComment ||
          !hasEscrituraNumber ||
          !hasCreditAmount ||
          !hasDate
        ) {
          setAlert!({
            severity: 'error',
            title: 'Error',
            description: 'Hacen falta campos requeridos',
          })
          setLoading(false)
        } else {
          await updateCredit(credit!.code, {
            phase: CreditPhases.Phase3,
            step: CreditSteps.Phase3Fondeo,
            isCollocated: true,
            meta: {
              finalSignatureDate: finalSignatureDateRef.current?.value,
              finalCreditAmount: finalCreditAmountRef.current?.value,
              escrituraNumber: escrituraNumberRef.current?.value,
            },
          }).then(async () => {
            const comment = `Avanzando a Etapa 3 - Fondeo :: ${commentRef.current?.value}`

            const finalSignatureDate = new Date(
              finalSignatureDateRef.current?.value || ''
            )
            finalSignatureDate.setDate(finalSignatureDate.getDate() + 1)

            const creditHistoryComment = {
              comment: comment,
              type: 'status',
              start: new Date(),
              meta: {
                finalSignatureDate: new Date(
                  finalSignatureDate
                ).toLocaleDateString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
                finalCreditAmount: formatCurrency(
                  parseFloat(finalCreditAmountRef.current?.value || '')
                ),
                escrituraNumber: escrituraNumberRef.current?.value || '',
              },
              change: {
                from: {
                  phase: credit.phase,
                  step: credit.step,
                },
                to: {
                  phase: CreditPhases.Phase3,
                  step: CreditSteps.Phase3Fondeo,
                },
              },
            }
            await addCreditHistory(credit.code, creditHistoryComment).then(
              () => {
                if (commentRef.current) {
                  commentRef.current.value = ''
                }
                refetch().then(() => {
                  setLoading(false)
                })
              }
            )
          })
        }
      }

      const handleSendBack = async () => {
        setLoading(true)

        await updateCredit(credit!.code, {
          step: CreditSteps.Phase3SignatureReady,
        }).then(async () => {
          const comment = `Regresando a Etapa 3 - Documentos de Escritura :: ${commentRef.current?.value}`
          const creditHistoryComment = {
            comment: comment,
            type: 'status',
            start: new Date(),
            change: {
              from: {
                owner: credit.owners.phase3Operator._id,
                step: credit.step,
              },
              to: {
                owner: credit.owners.advisor._id,
                step: CreditSteps.Phase3SignatureReady,
              },
            },
          }

          await addCreditHistory(credit!.code, creditHistoryComment).then(
            () => {
              if (commentRef.current) {
                commentRef.current.value = ''
              }
              refetch().then(() => {
                setLoading(false)
              })
            }
          )
        })
      }

      content = (
        <Fragment>
          <StyledDescription>
            El tramite fue escriturado, revisa la documentación y avanza para
            cobranza.
          </StyledDescription>
          <Input
            label="Numero de Escritura"
            ref={escrituraNumberRef}
            required
          />
          <NumericInput
            label="Credito Ejercido"
            ref={finalCreditAmountRef}
            required
          />
          <DateInput
            label="Fecha Final de Firma"
            ref={finalSignatureDateRef}
            required
          />
          <Textarea label="Comentarios" ref={commentRef} required />
        </Fragment>
      )

      footer = (
        <Container align="right" direction="row" padding>
          {loading ? (
            <Spinner type="bar" />
          ) : (
            <>
              {user?.role !== Role.Advisor && (
                <Button
                  color={color}
                  priority="secondary"
                  onClick={handleSendBack}
                  loading={loading}
                >
                  Regresar a Documentos de Escritura
                </Button>
              )}
              <Button onClick={handleSendToFondeo} color={color}>
                Mandar a Fondeo
              </Button>
            </>
          )}
        </Container>
      )
    } else {
      content = (
        <CreditWaitAction
          owner={credit.owners.phase3Operator}
          text="Felicidades, un crédito mas que logras!"
        />
      )
    }
  }

  return (
    <div>
      <Card header={header} footer={footer}>
        {content}
      </Card>
    </div>
  )
}

export default Phase3Escriturado
